import React, { CSSProperties, ReactNode, useCallback, useEffect, useRef } from 'react'
import { animated, useSpring } from '@react-spring/web'
import styled from 'styled-components'

import { Background } from 'Components/Background'

import { useCloseOnEscape } from 'Hooks/useCloseOnEscape'

import { spacingLG, spacingMD } from 'Utils/styles/spacing'
import { white } from 'Utils/styles/colors'
import { screenSizes } from 'Utils/styles/screenSizes'

import Close from 'Assets/close.svg'

const CloseModalButton = styled.img`
  cursor: pointer;
  position: absolute;
  top: ${spacingMD};
  right: ${spacingMD};
`

// Using snake case here because we get a warning when we use camel case
export const ModalContainer = styled(animated.div)<{
  overflow: 'auto' | 'hidden'
  min_width: number
  max_width?: number
}>`
  display: flex;
  flex-direction: column;
  gap: ${spacingMD};
  padding: ${spacingLG};
  max-height: calc(80vh);
  background-color: ${white};
  border-radius: 12px;
  overflow-y: ${({ overflow }) => overflow};
  max-width: ${({ max_width }) => (max_width ? `${max_width}px` : '')};
  min-width: ${({ min_width }) => `${min_width}px`};

  @media ${screenSizes.mobile} {
    min-width: inherit;
    width: calc(98vw - ${spacingLG}*2);
  }
`

type ModalProps = {
  showModal: boolean
  setShowModal: (a: boolean) => void
  children: ReactNode
  dataTestId?: string
  overflow?: boolean
  minWidth?: number
  maxWidth?: number
  style?: CSSProperties
  closeOnClickOutside?: boolean
}
export const Modal = ({
  showModal,
  setShowModal,
  children,
  maxWidth,
  dataTestId,
  style,
  minWidth = 500,
  overflow = false,
  closeOnClickOutside = false
}: ModalProps) => {
  const animation = useSpring({
    config: {
      duration: 250
    },
    opacity: showModal ? 1 : 0,
    transform: showModal ? `translateY(0%)` : `translateY(-100%)`
  })

  useCloseOnEscape(showModal, setShowModal)

  const ref = useRef<HTMLDivElement>(null)

  // onClick on Background (not the document-listener hook) so a nested modal on top doesn't close this one.
  const handleBackgroundClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) setShowModal(false)
    },
    [setShowModal]
  )

  useEffect(() => {
    if (showModal) {
      // avoid restoring 'hidden' when this modal renders on top of another one
      if (document.body.style.overflow === 'hidden') return

      const originalOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = originalOverflow || 'auto'
      }
    }
  }, [showModal])

  return (
    <>
      {showModal && (
        <Background onClick={closeOnClickOutside ? handleBackgroundClick : undefined}>
          <ModalContainer
            data-testid={dataTestId}
            overflow={overflow ? 'auto' : 'hidden'}
            min_width={minWidth}
            max_width={maxWidth}
            style={{ ...animation, ...style }}
            ref={ref}
          >
            {children}
            <CloseModalButton
              src={Close}
              alt="close"
              aria-label="Close modal"
              onClick={() => setShowModal(false)}
            />
          </ModalContainer>
        </Background>
      )}
    </>
  )
}
